// Traduce los cambios del feed de loan-config a los renglones de la sección 6 del mock.
import type { RecentParameterChange } from '@/api/services/loan-config/loan-config.client';
import { formatDateMx, formatTimeMx } from '@/lib/dateHelpers';
import { EXTRA_CATEGORY_LABEL, EXTRA_META, prettifyKey } from './extraParamsMeta';
import { PARAM_BINDINGS, toUiValue } from './mapping';
import { CATEGORY_LABEL, type ParamCategory, type ParamValue, type SystemParam } from './types';

export interface AuditRow {
    date: string;
    time: string;
    author: string;
    category: ParamCategory | null;
    categoryLabel: string;
    name: string;
    technicalKey: string;
    before: string;
    after: string;
    reason: string;
}

const PENDING_LABEL = '(pendiente)';
const NO_REASON_LABEL = '—';

/** Id de pantalla por `paramType#key`, para ubicar el cambio en su sección. */
const UI_ID_BY_TARGET = new Map<string, string>(
    Object.entries(PARAM_BINDINGS).flatMap(([id, binding]) =>
        binding ? [[`${binding.paramType}#${binding.key}`, id] as const] : [],
    ),
);

/** Valor legible: booleano como activo/inactivo, número y texto tal cual. */
export function formatParamValue(value: ParamValue): string {
    if (typeof value === 'boolean') return value ? 'activo' : 'inactivo';
    return String(value);
}

/** Un cambio del feed → renglón de la tabla, con nombre y unidades de la pantalla cuando hay binding. */
export function toAuditRow(
    change: RecentParameterChange,
    params: readonly SystemParam[],
): AuditRow {
    const target = `${change.paramType}#${change.key}`;
    const uiId = UI_ID_BY_TARGET.get(target);
    const binding = uiId ? PARAM_BINDINGS[uiId] : undefined;
    const param = uiId ? params.find((p) => p.id === uiId) : undefined;
    const show = (wire: string | null): string => {
        if (wire === null) return PENDING_LABEL;
        return binding ? formatParamValue(toUiValue(binding, wire)) : wire;
    };
    return {
        date: formatDateMx(change.at),
        time: formatTimeMx(change.at),
        author: change.performedBy,
        category: param?.category ?? null,
        categoryLabel: param
            ? CATEGORY_LABEL[param.category]
            : (EXTRA_CATEGORY_LABEL[change.paramType] ?? change.paramType),
        name: param?.name ?? EXTRA_META[target]?.name ?? prettifyKey(change.key),
        technicalKey: target,
        before: show(change.oldValue),
        after: show(change.newValue),
        reason: change.reason ?? NO_REASON_LABEL,
    };
}

/** «Última modificación»: la primera fila del feed; con la ventana vacía, el mayor `updatedAt`. */
export function lastModificationAt(
    items: readonly RecentParameterChange[],
    latestUpdatedAt: string | null,
): string | null {
    return items[0]?.at ?? latestUpdatedAt;
}
